import { useCallback, useEffect, useMemo, useState } from 'react';
import GridTable from '../../table/GridTable';
import { ColDef } from 'ag-grid-community';
import {
  IFortuneLabUserItem,
  useFortuneLabPerformanceStore,
} from '../../../store/useFortuneLabPerformance';
import { useFortuneLabCodeStore } from '../../../store/useFortuneLabCode';
import { useLoginStore } from '../../../store/useLogin';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { formatDate, updateUrlWithParams } from '../../../util/util';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faTrash } from '@fortawesome/free-solid-svg-icons';
import PerformanceFilter from './filter/PerformanceFilter';
import ConfirmDialog from '../../modal/ConfirmDialog';
import apiClient from '../../../service/apiClient';
import { toast } from 'react-toastify';

const toStartDateTimeString = (value: Date | null) =>
  value ? formatDate(value)['yyyy-mm-ddTHH:mm:ss'] : undefined;

const toEndDateTimeString = (value: Date | null) => {
  if (!value) return undefined;
  const end = new Date(value);
  end.setHours(23, 59, 59, 999);
  return formatDate(end)['yyyy-mm-ddTHH:mm:ss'];
};

const PerformancePage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { resultList, resultPage, fetchResults, deleteUser } = useFortuneLabPerformanceStore();
  const { codeList, fetchCodes } = useFortuneLabCodeStore();
  const { role } = useLoginStore();
  const isAdmin = role === 'ROLE_ADMIN';

  const defaultStartDate = useMemo(() => {
    const start = new Date();
    start.setDate(start.getDate() - 7);
    start.setHours(0, 0, 0, 0);
    return start;
  }, []);

  const defaultEndDate = useMemo(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return end;
  }, []);

  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate);
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate);
  const [selectedTrkId, setSelectedTrkId] = useState<string>('');
  const [searchName, setSearchName] = useState<string>('');
  const [searchPhone, setSearchPhone] = useState<string>('');
  const [pageSize, setPageSize] = useState<string>('20');

  const [deleteTarget, setDeleteTarget] = useState<IFortuneLabUserItem | null>(null);

  useEffect(() => {
    fetchCodes(0, 100);
  }, [fetchCodes]);

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    const trkId = (parsed.trkId as string) || '';
    const name = (parsed.name as string) || '';
    const phoneNumber = (parsed.phoneNumber as string) || '';
    const startDateTime = (parsed.startDateTime as string) || undefined;
    const endDateTime = (parsed.endDateTime as string) || undefined;
    const page = parsed.page ? Number(parsed.page) : 0;
    const size = parsed.size ? String(parsed.size) : '20';

    setSelectedTrkId(trkId);
    setSearchName(name);
    setSearchPhone(phoneNumber);
    setPageSize(size);

    const parsedStartDate = startDateTime ? new Date(startDateTime) : defaultStartDate;
    const parsedEndDate = endDateTime ? new Date(endDateTime) : defaultEndDate;
    setStartDate(parsedStartDate);
    setEndDate(parsedEndDate);

    fetchResults(
      trkId || undefined,
      name || undefined,
      phoneNumber || undefined,
      startDateTime || toStartDateTimeString(parsedStartDate),
      endDateTime || toEndDateTimeString(parsedEndDate),
      page,
      Number(size)
    );
  }, [defaultStartDate, defaultEndDate, fetchResults, location.search]);

  const handleSearch = useCallback(() => {
    const startString = toStartDateTimeString(startDate);
    const endString = toEndDateTimeString(endDate);
    const sizeNumber = Number(pageSize);

    updateUrlWithParams(
      {
        trkId: selectedTrkId || undefined,
        name: searchName.trim() || undefined,
        phoneNumber: searchPhone.trim() || undefined,
        startDateTime: startString,
        endDateTime: endString,
        page: '0',
        size: pageSize,
      },
      navigate
    );

    fetchResults(
      selectedTrkId || undefined,
      searchName.trim() || undefined,
      searchPhone.trim() || undefined,
      startString,
      endString,
      0,
      sizeNumber
    );
  }, [startDate, endDate, pageSize, selectedTrkId, searchName, searchPhone, navigate, fetchResults]);

  const handlePageSizeChange = useCallback(
    (value: string) => {
      setPageSize(value);

      const startString = toStartDateTimeString(startDate);
      const endString = toEndDateTimeString(endDate);

      updateUrlWithParams(
        {
          trkId: selectedTrkId || undefined,
          name: searchName.trim() || undefined,
          phoneNumber: searchPhone.trim() || undefined,
          startDateTime: startString,
          endDateTime: endString,
          page: '0',
          size: value,
        },
        navigate
      );

      fetchResults(
        selectedTrkId || undefined,
        searchName.trim() || undefined,
        searchPhone.trim() || undefined,
        startString,
        endString,
        0,
        Number(value)
      );
    },
    [startDate, endDate, fetchResults, navigate, selectedTrkId, searchName, searchPhone]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      if (!resultPage) return;

      const startString = toStartDateTimeString(startDate);
      const endString = toEndDateTimeString(endDate);
      const sizeNumber = Number(pageSize);

      updateUrlWithParams(
        {
          trkId: selectedTrkId || undefined,
          name: searchName.trim() || undefined,
          phoneNumber: searchPhone.trim() || undefined,
          startDateTime: startString,
          endDateTime: endString,
          page: page.toString(),
          size: pageSize,
        },
        navigate
      );

      fetchResults(
        selectedTrkId || undefined,
        searchName.trim() || undefined,
        searchPhone.trim() || undefined,
        startString,
        endString,
        page,
        sizeNumber
      );
    },
    [startDate, endDate, pageSize, selectedTrkId, searchName, searchPhone, resultPage, navigate, fetchResults]
  );

  const handleDownloadExcel = useCallback(async () => {
    const startString = toStartDateTimeString(startDate);
    const endString = toEndDateTimeString(endDate);

    try {
      const params: Record<string, string | undefined> = {
        trkId: selectedTrkId || undefined,
        name: searchName.trim() || undefined,
        phoneNumber: searchPhone.trim() || undefined,
        startDateTime: startString,
        endDateTime: endString,
      };

      const response = await apiClient.get<Blob>('/fortune-lab/users/export', {
        params,
        responseType: 'blob' as const,
      });

      const blob = response.data;

      const fileName = `fortune-lab-users-${formatDate(new Date()).yyyymmddhhmmss}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('엑셀 다운로드가 완료되었습니다.');
    } catch (error) {
      console.error('fortune-lab excel download error', error);
      toast.error('엑셀 다운로드에 실패했습니다.');
    }
  }, [startDate, endDate, selectedTrkId, searchName, searchPhone]);

  useEffect(() => {
    if (resultPage) {
      const currentPage = resultPage.pageable.pageNumber;
      const totalPages = resultPage.totalPages;
      if (currentPage >= totalPages && totalPages > 0) {
        handlePageChange(totalPages - 1);
      }
    }
  }, [resultPage, handlePageChange]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const ok = await deleteUser(deleteTarget.id);
    if (ok) {
      // 현재 페이지 다시 조회
      const startString = toStartDateTimeString(startDate);
      const endString = toEndDateTimeString(endDate);
      const sizeNumber = Number(pageSize);
      const currentPage = resultPage?.pageable.pageNumber ?? 0;
      fetchResults(
        selectedTrkId || undefined,
        searchName.trim() || undefined,
        searchPhone.trim() || undefined,
        startString,
        endString,
        currentPage,
        sizeNumber
      );
    }
    setDeleteTarget(null);
  }, [deleteTarget, deleteUser, fetchResults, resultPage, startDate, endDate, pageSize, selectedTrkId, searchName, searchPhone]);

  const columns: ColDef<IFortuneLabUserItem>[] = useMemo(
    () => {
      const cols: ColDef<IFortuneLabUserItem>[] = [
        {
          headerName: '이름',
          field: 'name',
          flex: 1,
        },
        {
          headerName: '전화번호',
          field: 'phoneNumber',
          flex: 1.5,
        },
        {
          headerName: '생년월일',
          field: 'birthDate',
          flex: 1,
        },
        {
          headerName: '음양력',
          field: 'solarType',
          flex: 0.8,
          valueFormatter: params => params.value ?? '양력',
        },
        {
          headerName: '일주',
          field: 'birthDayJu',
          flex: 0.8,
        },
        {
          headerName: '트래킹 코드',
          field: 'trkId',
          flex: 1,
        },
        {
          headerName: '캠페인명',
          field: 'campaignName',
          flex: 1.5,
          valueFormatter: params => {
            const value = params.value as string | undefined;
            return value && value.trim() ? value : '-';
          },
        },
      ];

      cols.push(
        {
          headerName: '최초 등록',
          field: 'createdAt',
          flex: 1.5,
          valueFormatter: params =>
            params.value ? new Date(params.value).toLocaleString() : '',
        },
        {
          headerName: '최근 조회',
          field: 'updatedAt',
          flex: 1.5,
          valueFormatter: params =>
            params.value ? new Date(params.value).toLocaleString() : '',
        }
      );

      // ADMIN 전용: 삭제 버튼
      if (isAdmin) {
        cols.push({
          headerName: '관리',
          width: 90,
          cellRenderer: (params: any) => {
            const row: IFortuneLabUserItem | undefined = params.data;
            if (!row) return null;
            return (
              <div className="!flex !items-center !h-full">
                <button
                  className="!px-2 !py-1 !rounded !text-xs !bg-red-500 hover:!bg-red-600 !text-white !flex !items-center !gap-1"
                  onClick={() => setDeleteTarget(row)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  삭제
                </button>
              </div>
            );
          },
        });
      }

      return cols;
    },
    [isAdmin]
  );

  return (
    <div className="!w-full !h-full !flex !flex-col">
      <div className="!flex !items-center !justify-between !py-4">
        <PerformanceFilter
          trkId={selectedTrkId}
          onTrkIdChange={setSelectedTrkId}
          name={searchName}
          onNameChange={setSearchName}
          phoneNumber={searchPhone}
          onPhoneNumberChange={setSearchPhone}
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          onSubmit={handleSearch}
          onDownload={handleDownloadExcel}
          codes={codeList}
          showAdminFilters={isAdmin}
        />
      </div>

      <div className="!flex-1 !min-h-0">
        <GridTable<IFortuneLabUserItem>
          height="100%"
          rowData={resultList}
          columnDefs={columns}
        />
      </div>

      {resultPage && resultPage.totalPages > 0 && (
        <div className="!w-full !mt-6">
          <div className="!flex !justify-center !items-center !space-x-2">
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() => handlePageChange(0)}
              disabled={resultPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.max(0, resultPage.pageable.pageNumber - 1)
                )
              }
              disabled={resultPage.pageable.pageNumber === 0}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <span className="!text-sm !text-gray-600">
              {resultPage.pageable.pageNumber + 1} / {resultPage.totalPages || 1}
            </span>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(
                  Math.min(
                    resultPage.totalPages - 1,
                    resultPage.pageable.pageNumber + 1
                  )
                )
              }
              disabled={resultPage.pageable.pageNumber >= resultPage.totalPages - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button
              className="!px-2 !py-1 !border !border-gray-300 !rounded hover:!bg-gray-50 disabled:!opacity-50"
              onClick={() =>
                handlePageChange(Math.max(resultPage.totalPages - 1, 0))
              }
              disabled={resultPage.pageable.pageNumber >= resultPage.totalPages - 1}
            >
              <FontAwesomeIcon icon={faChevronRight} />
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>
      )}

      {/* 삭제 확인 다이얼로그 */}
      <ConfirmDialog
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="신청 내역 삭제"
        message=""
        confirmText="삭제"
        cancelText="취소"
        type="danger"
        messageNode={
          deleteTarget ? (
            <div className="!flex !flex-col !gap-2 !text-left">
              <p className="!text-sm !text-gray-700">
                아래 신청 내역을 삭제하시겠습니까?
              </p>
              <div className="!mt-2 !p-3 !bg-gray-50 !rounded !border !border-gray-200">
                <div className="!grid !grid-cols-2 !gap-2 !text-sm">
                  <span className="!text-gray-500">이름</span>
                  <span className="!text-gray-900 !font-medium">{deleteTarget.name}</span>
                  <span className="!text-gray-500">전화번호</span>
                  <span className="!text-gray-900">{deleteTarget.phoneNumber}</span>
                  <span className="!text-gray-500">캠페인</span>
                  <span className="!text-gray-900">{deleteTarget.campaignName || '-'}</span>
                </div>
              </div>
              <p className="!mt-2 !text-xs !font-semibold !text-red-600">
                ⚠️ 삭제된 신청 내역은 복구할 수 없습니다.
              </p>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default PerformancePage;
